import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { entities } from "../services/api";
import { toast } from "../components/Toast";
import {
  BackIcon,
  OrganizeIcon,
  ProductIcon,
  ContainerIcon,
  LocationIcon,
  DocumentIcon,
  SearchIcon,
  PlusIcon,
  HomeIcon,
  CameraIcon,
  UserIcon,
  CheckIcon,
  InfoIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "../components/Icons";

const ItemTypes = {
  ENTITY: "entity",
};

function DraggableItem({ item, children, canDrag = true }) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.ENTITY,
      item: () => item,
      canDrag: canDrag,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [item, canDrag],
  );

  return (
    <div
      ref={drag}
      style={{
        cursor: canDrag ? (isDragging ? "grabbing" : "grab") : "default",
      }}
    >
      {children}
    </div>
  );
}

function DroppableItem({ item, onDrop, canDrop, children }) {
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: ItemTypes.ENTITY,
      canDrop: (draggedItem) => {
        if (!canDrop || draggedItem.id === item.id) {
          return false;
        }

        // Validation
        if (
          draggedItem.entity_type === "product" &&
          (item.entity_type === "container" || item.entity_type === "location")
        ) {
          return true;
        }
        if (
          draggedItem.entity_type === "container" &&
          item.entity_type === "location"
        ) {
          return true;
        }
        if (
          draggedItem.entity_type === "location" &&
          item.entity_type === "location"
        ) {
          return true;
        }
        return false;
      },
      drop: (draggedItem) => {
        onDrop(draggedItem, item);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver() && monitor.canDrop(),
      }),
    }),
    [item, canDrop, onDrop],
  );

  return (
    <div
      ref={drop}
      style={{
        background: isOver ? "rgba(59, 130, 246, 0.15)" : "transparent",
        borderLeft: isOver
          ? "3px solid var(--accent-primary)"
          : "3px solid transparent",
        transition: "background 0.2s ease, border-left 0.2s ease",
      }}
    >
      {children}
    </div>
  );
}

const PAGE_SIZE = 50;

function Organize() {
  const navigate = useNavigate();
  const [allEntities, setAllEntities] = useState([]);
  const [products, setProducts] = useState([]);
  const [containers, setContainers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState({
    products: true,
    containers: true,
    locations: true,
  });
  const [hasMoreUnorganized, setHasMoreUnorganized] = useState({
    products: true,
    containers: true,
  });
  const [searchLocations, setSearchLocations] = useState("");
  const [searchContainers, setSearchContainers] = useState("");
  const [searchUnorganized, setSearchUnorganized] = useState("");
  const [undoStack, setUndoStack] = useState([]); // [{itemId, itemName, fromParentId, toParentId}]
  const [selectedItems, setSelectedItems] = useState(new Set()); // For multi-select
  const searchDebounceRef = useRef({});
  // Offsets for paged fetches only — list lengths get polluted by tree
  // expansion and searches, so they can't be used as offsets
  const pageOffsetsRef = useRef({
    product: 0,
    container: 0,
    location: 0,
    unorganizedProduct: 0,
    unorganizedContainer: 0,
  });

  useEffect(() => {
    loadEntities();
  }, []);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(searchDebounceRef.current).forEach(clearTimeout);
    };
  }, []);

  const loadEntities = async (searches = {}) => {
    setLoading(true);
    try {
      // Load all entity types in parallel with pagination
      const productParams = {
        entity_type: "product",
        limit: PAGE_SIZE,
        offset: 0,
      };
      const containerParams = {
        entity_type: "container",
        limit: PAGE_SIZE,
        offset: 0,
      };
      const locationParams = {
        entity_type: "location",
        limit: PAGE_SIZE,
        offset: 0,
      };

      if (searches.products) productParams.search = searches.products;
      if (searches.containers) containerParams.search = searches.containers;
      if (searches.locations) locationParams.search = searches.locations;

      const [
        productsRes,
        containersRes,
        locationsRes,
        unorgProductsRes,
        unorgContainersRes,
      ] = await Promise.all([
        entities.list(productParams),
        entities.list(containerParams),
        entities.list(locationParams),
        // Fetch unorganized items directly so the Unorganized column isn't
        // limited to whatever happens to be in the newest unfiltered page
        entities.list({
          entity_type: "product",
          limit: PAGE_SIZE,
          offset: 0,
          parent_id: "null",
        }),
        entities.list({
          entity_type: "container",
          limit: PAGE_SIZE,
          offset: 0,
          parent_id: "null",
        }),
      ]);

      const productIds = new Set(productsRes.data.map((e) => e.id));
      const mergedProducts = [
        ...productsRes.data,
        ...unorgProductsRes.data.filter((e) => !productIds.has(e.id)),
      ];
      const containerIds = new Set(containersRes.data.map((e) => e.id));
      const mergedContainers = [
        ...containersRes.data,
        ...unorgContainersRes.data.filter((e) => !containerIds.has(e.id)),
      ];

      setAllEntities([
        ...mergedProducts,
        ...mergedContainers,
        ...locationsRes.data,
      ]);
      setProducts(mergedProducts);
      setContainers(mergedContainers);
      setLocations(locationsRes.data);
      pageOffsetsRef.current = {
        product: productsRes.data.length,
        container: containersRes.data.length,
        location: locationsRes.data.length,
        unorganizedProduct: unorgProductsRes.data.length,
        unorganizedContainer: unorgContainersRes.data.length,
      };
      setHasMore({
        products: productsRes.data.length === PAGE_SIZE,
        containers: containersRes.data.length === PAGE_SIZE,
        locations: locationsRes.data.length === PAGE_SIZE,
      });
      setHasMoreUnorganized({
        products: unorgProductsRes.data.length === PAGE_SIZE,
        containers: unorgContainersRes.data.length === PAGE_SIZE,
      });
    } catch (err) {
      console.error("Failed to load entities:", err);
    } finally {
      setLoading(false);
    }
  };

  // Search a specific entity type
  const searchEntityType = useCallback(async (entityType, searchTerm) => {
    try {
      const params = { entity_type: entityType, limit: PAGE_SIZE, offset: 0 };
      if (searchTerm) params.search = searchTerm;

      const { data } = await entities.list(params);

      if (entityType === "product") {
        setProducts(data);
        setAllEntities((prev) => {
          const nonProducts = prev.filter((e) => e.entity_type !== "product");
          return [...nonProducts, ...data];
        });
        pageOffsetsRef.current.product = data.length;
        setHasMore((prev) => ({
          ...prev,
          products: data.length === PAGE_SIZE,
        }));
      } else if (entityType === "container") {
        setContainers(data);
        setAllEntities((prev) => {
          const nonContainers = prev.filter(
            (e) => e.entity_type !== "container",
          );
          return [...nonContainers, ...data];
        });
        pageOffsetsRef.current.container = data.length;
        setHasMore((prev) => ({
          ...prev,
          containers: data.length === PAGE_SIZE,
        }));
      } else if (entityType === "location") {
        setLocations(data);
        setAllEntities((prev) => {
          const nonLocations = prev.filter((e) => e.entity_type !== "location");
          return [...nonLocations, ...data];
        });
        pageOffsetsRef.current.location = data.length;
        setHasMore((prev) => ({
          ...prev,
          locations: data.length === PAGE_SIZE,
        }));
      }
    } catch (err) {
      console.error(`Failed to search ${entityType}s:`, err);
    }
  }, []);

  // Debounced search handlers
  const handleSearchLocations = useCallback(
    (value) => {
      setSearchLocations(value);
      if (searchDebounceRef.current.locations) {
        clearTimeout(searchDebounceRef.current.locations);
      }
      searchDebounceRef.current.locations = setTimeout(() => {
        searchEntityType("location", value);
      }, 300);
    },
    [searchEntityType],
  );

  const handleSearchContainers = useCallback(
    (value) => {
      setSearchContainers(value);
      if (searchDebounceRef.current.containers) {
        clearTimeout(searchDebounceRef.current.containers);
      }
      searchDebounceRef.current.containers = setTimeout(() => {
        searchEntityType("container", value);
      }, 300);
    },
    [searchEntityType],
  );

  // Search unorganized items (products and containers without parents)
  const searchUnorganizedItems = useCallback(async (searchTerm) => {
    try {
      const productParams = {
        entity_type: "product",
        limit: PAGE_SIZE,
        offset: 0,
        parent_id: "null",
      };
      const containerParams = {
        entity_type: "container",
        limit: PAGE_SIZE,
        offset: 0,
        parent_id: "null",
      };

      if (searchTerm) {
        productParams.search = searchTerm;
        containerParams.search = searchTerm;
      }

      const [productsRes, containersRes] = await Promise.all([
        entities.list(productParams),
        entities.list(containerParams),
      ]);

      setProducts((prev) => {
        // Keep organized products, replace unorganized ones
        const organized = prev.filter((p) => p.parent_id);
        return [...organized, ...productsRes.data];
      });
      setContainers((prev) => {
        // Keep organized containers, replace unorganized ones
        const organized = prev.filter((c) => c.parent_id);
        return [...organized, ...containersRes.data];
      });
      setAllEntities((prev) => {
        const nonProductsContainers = prev.filter(
          (e) => e.entity_type === "location",
        );
        const organizedProducts = prev.filter(
          (e) => e.entity_type === "product" && e.parent_id,
        );
        const organizedContainers = prev.filter(
          (e) => e.entity_type === "container" && e.parent_id,
        );
        return [
          ...nonProductsContainers,
          ...organizedProducts,
          ...organizedContainers,
          ...productsRes.data,
          ...containersRes.data,
        ];
      });
      pageOffsetsRef.current.unorganizedProduct = productsRes.data.length;
      pageOffsetsRef.current.unorganizedContainer = containersRes.data.length;
      setHasMoreUnorganized({
        products: productsRes.data.length === PAGE_SIZE,
        containers: containersRes.data.length === PAGE_SIZE,
      });
    } catch (err) {
      console.error("Failed to search unorganized:", err);
    }
  }, []);

  const handleSearchUnorganized = useCallback(
    (value) => {
      setSearchUnorganized(value);
      if (searchDebounceRef.current.unorganized) {
        clearTimeout(searchDebounceRef.current.unorganized);
      }
      searchDebounceRef.current.unorganized = setTimeout(() => {
        searchUnorganizedItems(value);
      }, 300);
    },
    [searchUnorganizedItems],
  );

  const loadMore = async (entityType, options = {}) => {
    const offsetKey = options.unorganizedOnly
      ? entityType === "product"
        ? "unorganizedProduct"
        : "unorganizedContainer"
      : entityType;
    const more = options.unorganizedOnly
      ? hasMoreUnorganized[entityType + "s"]
      : hasMore[entityType + "s"];
    if (loadingMore || !more) return;
    setLoadingMore(true);
    try {
      const params = {
        entity_type: entityType,
        limit: PAGE_SIZE,
        offset: pageOffsetsRef.current[offsetKey],
      };

      // For unorganized items, filter by no parent
      if (options.unorganizedOnly) {
        params.parent_id = "null";
      }

      const { data } = await entities.list(params);
      pageOffsetsRef.current[offsetKey] += data.length;

      // Dedupe: rows may already be loaded via tree expansion or the
      // unorganized fetch
      const appendNew = (prev) => {
        const existingIds = new Set(prev.map((e) => e.id));
        return [...prev, ...data.filter((e) => !existingIds.has(e.id))];
      };

      if (entityType === "product") {
        setProducts(appendNew);
      } else if (entityType === "container") {
        setContainers(appendNew);
      } else {
        setLocations(appendNew);
      }
      setAllEntities(appendNew);

      if (options.unorganizedOnly) {
        setHasMoreUnorganized((prev) => ({
          ...prev,
          [entityType + "s"]: data.length === PAGE_SIZE,
        }));
      } else {
        setHasMore((prev) => ({
          ...prev,
          [entityType + "s"]: data.length === PAGE_SIZE,
        }));
      }
    } catch (err) {
      console.error(`Failed to load more ${entityType}s:`, err);
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleExpand = async (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
      // Load all descendants from API recursively
      try {
        const loadDescendants = async (parentIds) => {
          if (parentIds.length === 0) return [];

          // Fetch children of all parents in parallel
          const results = await Promise.all(
            parentIds.map((pid) =>
              entities.list({ parent_id: pid, limit: -1 }),
            ),
          );

          const allChildren = results.flatMap((r) => r.data || []);

          // Find containers and locations that might have their own children
          const containerAndLocationIds = allChildren
            .filter(
              (c) =>
                c.entity_type === "container" || c.entity_type === "location",
            )
            .map((c) => c.id);

          // Recursively load grandchildren
          if (containerAndLocationIds.length > 0) {
            const grandchildren = await loadDescendants(
              containerAndLocationIds,
            );
            return [...allChildren, ...grandchildren];
          }

          return allChildren;
        };

        const descendants = await loadDescendants([id]);

        if (descendants.length > 0) {
          setAllEntities((prev) => {
            const existingIds = new Set(prev.map((e) => e.id));
            const newItems = descendants.filter((c) => !existingIds.has(c.id));
            return [...prev, ...newItems];
          });
          // Also update type-specific lists
          const newProducts = descendants.filter(
            (c) => c.entity_type === "product",
          );
          const newContainers = descendants.filter(
            (c) => c.entity_type === "container",
          );
          const newLocations = descendants.filter(
            (c) => c.entity_type === "location",
          );
          if (newProducts.length > 0) {
            setProducts((prev) => {
              const existingIds = new Set(prev.map((e) => e.id));
              return [
                ...prev,
                ...newProducts.filter((p) => !existingIds.has(p.id)),
              ];
            });
          }
          if (newContainers.length > 0) {
            setContainers((prev) => {
              const existingIds = new Set(prev.map((e) => e.id));
              return [
                ...prev,
                ...newContainers.filter((c) => !existingIds.has(c.id)),
              ];
            });
          }
          if (newLocations.length > 0) {
            setLocations((prev) => {
              const existingIds = new Set(prev.map((e) => e.id));
              return [
                ...prev,
                ...newLocations.filter((l) => !existingIds.has(l.id)),
              ];
            });
          }
        }
      } catch (err) {
        console.error("Failed to load descendants:", err);
      }
    }
    setExpandedItems(newExpanded);
  };

  const getChildren = (parentId) => {
    return allEntities.filter((e) => e.parent_id === parentId);
  };

  const handleDrop = async (draggedItem, targetItem) => {
    // If dragged item is selected, move all selected items
    if (selectedItems.has(draggedItem.id)) {
      await moveSelectedItems(targetItem.id);
    } else {
      await moveItem(draggedItem.id, targetItem.id);
    }
  };

  const handleDropUnorganized = async (draggedItem) => {
    if (selectedItems.has(draggedItem.id)) {
      // Move all selected items to unorganized
      const itemsToMove = Array.from(selectedItems);
      clearSelection();
      for (const itemId of itemsToMove) {
        const item = allEntities.find((e) => e.id === itemId);
        if (item?.parent_id) {
          await moveItem(itemId, null);
        }
      }
    } else if (draggedItem.parent_id) {
      await moveItem(draggedItem.id, null);
    }
  };

  const moveItem = async (itemId, parentId, isUndo = false) => {
    // Never move an item into itself
    if (itemId === parentId) return;

    // Find item to get its name and previous parent
    const item = allEntities.find((e) => e.id === itemId);
    const previousParentId = item?.parent_id || null;

    // Optimistically update local state
    setAllEntities((prev) =>
      prev.map((e) => (e.id === itemId ? { ...e, parent_id: parentId } : e)),
    );
    setProducts((prev) =>
      prev.map((e) => (e.id === itemId ? { ...e, parent_id: parentId } : e)),
    );
    setContainers((prev) =>
      prev.map((e) => (e.id === itemId ? { ...e, parent_id: parentId } : e)),
    );
    setLocations((prev) =>
      prev.map((e) => (e.id === itemId ? { ...e, parent_id: parentId } : e)),
    );

    if (parentId) {
      setExpandedItems((prev) => new Set([...prev, parentId]));
    }

    // Add to undo stack (unless this is an undo operation)
    let stackBeforeMove = null;
    if (!isUndo && item) {
      setUndoStack((prev) => {
        stackBeforeMove = prev;
        // Check if this move cancels out a previous move (moving back to original position)
        const lastMoveIndex = prev.findLastIndex((a) => a.itemId === itemId);
        if (lastMoveIndex !== -1) {
          const lastMove = prev[lastMoveIndex];
          // If moving back to where it originally was, remove that entry instead of adding new one
          if (lastMove.fromParentId === parentId) {
            return prev.filter((_, i) => i !== lastMoveIndex);
          }
        }
        return [
          ...prev,
          {
            itemId,
            itemName: item.name,
            fromParentId: previousParentId,
            toParentId: parentId,
          },
        ];
      });
    }

    try {
      await entities.move(itemId, parentId);
    } catch (err) {
      console.error("Failed to move item:", err);
      toast.error("Failed to move item");
      // Revert on error
      loadEntities();
      // Restore the undo stack to its pre-move state (the mutation above may
      // have removed a cancelled-out entry rather than appended one)
      if (!isUndo && stackBeforeMove) {
        setUndoStack(stackBeforeMove);
      }
    }
  };

  const handleUndo = async (index) => {
    const action = undoStack[index];
    if (!action) return;

    // Remove this action and all after it from the stack
    setUndoStack((prev) => prev.slice(0, index));

    // Move item back to previous parent
    await moveItem(action.itemId, action.fromParentId, true);
  };

  const handleUndoAll = async () => {
    // Process in reverse order
    const actions = [...undoStack].reverse();
    setUndoStack([]);

    for (const action of actions) {
      await moveItem(action.itemId, action.fromParentId, true);
    }
  };

  const toggleSelectItem = (itemId) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const moveSelectedItems = async (targetId) => {
    // Never move the drop target into itself
    const itemsToMove = Array.from(selectedItems).filter(
      (id) => id !== targetId,
    );
    clearSelection();

    for (const itemId of itemsToMove) {
      await moveItem(itemId, targetId);
    }
  };

  const itemMatchesSearch = (item, searchTerm, checkChildren = true) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();

    if (item.name.toLowerCase().includes(term)) return true;

    if (checkChildren && item.entity_type === "location") {
      const children = getChildren(item.id);
      const containers = children.filter((e) => e.entity_type === "container");
      const directProducts = children.filter(
        (e) => e.entity_type === "product",
      );
      return (
        containers.some((child) => child.name.toLowerCase().includes(term)) ||
        directProducts.some((child) => child.name.toLowerCase().includes(term))
      );
    }

    if (checkChildren && item.entity_type === "container") {
      const children = getChildren(item.id);
      return children.some((child) =>
        itemMatchesSearch(child, searchTerm, true),
      );
    }

    return false;
  };

  const getIcon = (type) => {
    switch (type) {
      case "product":
        return <ProductIcon size={20} />;
      case "container":
        return <ContainerIcon size={20} />;
      case "location":
        return <LocationIcon size={20} />;
      default:
        return <DocumentIcon size={20} />;
    }
  };

  const getColor = (type) => {
    switch (type) {
      case "product":
        return "badge-product";
      case "container":
        return "badge-container";
      case "location":
        return "badge-location";
      default:
        return "badge-product";
    }
  };

  const renderLocationTreeItem = (item, level = 0) => {
    const directChildren = getChildren(item.id);
    const locationChildren = directChildren.filter(
      (e) => e.entity_type === "location",
    );
    const containerChildren = directChildren.filter(
      (e) => e.entity_type === "container",
    );
    const directProducts = directChildren.filter(
      (e) => e.entity_type === "product",
    );

    // Server-side search handles filtering
    const filteredLocations = locationChildren;
    const filteredContainers = containerChildren;
    const filteredProducts = directProducts;

    const hasChildren = directChildren.length > 0;
    const hasFilteredChildren =
      filteredLocations.length > 0 ||
      filteredContainers.length > 0 ||
      filteredProducts.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const canDrag =
      item.entity_type === "product" ||
      item.entity_type === "container" ||
      item.entity_type === "location";
    const canDrop = item.entity_type === "location";
    const isSelected = selectedItems.has(item.id);

    // Top-level items came from the server search, which already matched
    // name OR description — don't re-filter them by name only
    if (
      level > 0 &&
      searchLocations &&
      !itemMatchesSearch(item, searchLocations)
    ) {
      return null;
    }

    const handleClick = (e) => {
      if ((e.shiftKey || e.ctrlKey || e.metaKey) && canDrag) {
        e.preventDefault();
        e.stopPropagation();
        toggleSelectItem(item.id);
      } else if (selectedItems.size > 0) {
        clearSelection();
      }
    };

    const canDragItem = canDrag && (selectedItems.size === 0 || isSelected);

    const itemContent = (
      <div
        className="list-item card-compact"
        style={{
          marginLeft: `${level * 2}rem`,
          userSelect: "none",
          background: isSelected ? "rgba(59, 130, 246, 0.15)" : undefined,
          borderColor: isSelected ? "var(--accent-primary)" : undefined,
          position: "relative",
        }}
        onClick={handleClick}
      >
        {(item.entity_type === "location" ||
          item.entity_type === "container") &&
        (hasChildren || !expandedItems.has(item.id)) ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(item.id);
            }}
            className="btn btn-ghost btn-icon"
            style={{
              padding: "0.25rem",
              minHeight: "auto",
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon size={16} />
            ) : (
              <ChevronRightIcon size={16} />
            )}
          </button>
        ) : (
          <span style={{ width: "24px", display: "inline-block" }}></span>
        )}
        <div className="list-item-content">
          <div className="list-item-title">{item.name}</div>
          <div className="list-item-subtitle">
            <span className={`badge ${getColor(item.entity_type)}`}>
              {item.entity_type}
            </span>
            {hasChildren && item.entity_type === "location" && (
              <span
                style={{
                  marginLeft: "var(--spacing-sm)",
                  color: "var(--text-muted)",
                }}
              >
                {directChildren.length} items
              </span>
            )}
            {item.entity_type === "container" &&
              getChildren(item.id).length > 0 && (
                <span
                  style={{
                    marginLeft: "var(--spacing-sm)",
                    color: "var(--text-muted)",
                  }}
                >
                  {getChildren(item.id).length} items
                </span>
              )}
          </div>
        </div>
        {isSelected && selectedItems.size > 1 && (
          <div
            style={{
              position: "absolute",
              top: "-6px",
              right: "-6px",
              background: "var(--accent-primary)",
              color: "white",
              borderRadius: "50%",
              width: "20px",
              height: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          >
            {selectedItems.size}
          </div>
        )}
      </div>
    );

    return (
      <div key={item.id} style={{ marginBottom: "var(--spacing-xs)" }}>
        <DraggableItem item={item} canDrag={canDragItem}>
          <DroppableItem item={item} onDrop={handleDrop} canDrop={canDrop}>
            {itemContent}
          </DroppableItem>
        </DraggableItem>
        {isExpanded &&
          (item.entity_type === "location" ||
            item.entity_type === "container") && (
            <div>
              {filteredLocations.map((loc) =>
                renderLocationTreeItem(loc, level + 1),
              )}
              {filteredContainers.map((cont) =>
                renderLocationTreeItem(cont, level + 1),
              )}
              {filteredProducts.map((prod) =>
                renderLocationTreeItem(prod, level + 1),
              )}
            </div>
          )}
      </div>
    );
  };

  const renderContainerTreeItem = (item, level = 0) => {
    const children = getChildren(item.id);
    const filteredChildren = children.filter((c) =>
      itemMatchesSearch(c, searchContainers),
    );

    const hasChildren = children.length > 0;
    const hasFilteredChildren = filteredChildren.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const canDrag =
      item.entity_type === "product" || item.entity_type === "container";
    const canDrop = item.entity_type === "container";
    const isSelected = selectedItems.has(item.id);

    // Top-level items came from the server search, which already matched
    // name OR description — don't re-filter them by name only
    if (
      level > 0 &&
      searchContainers &&
      !itemMatchesSearch(item, searchContainers)
    ) {
      return null;
    }

    const handleClick = (e) => {
      if ((e.shiftKey || e.ctrlKey || e.metaKey) && canDrag) {
        e.preventDefault();
        e.stopPropagation();
        toggleSelectItem(item.id);
      } else if (selectedItems.size > 0) {
        clearSelection();
      }
    };

    const canDragItem = canDrag && (selectedItems.size === 0 || isSelected);

    const itemContent = (
      <div
        className="list-item card-compact"
        style={{
          marginLeft: `${level * 2}rem`,
          userSelect: "none",
          background: isSelected ? "rgba(59, 130, 246, 0.15)" : undefined,
          borderColor: isSelected ? "var(--accent-primary)" : undefined,
          position: "relative",
        }}
        onClick={handleClick}
      >
        {item.entity_type === "container" &&
        (hasChildren || !expandedItems.has(item.id)) ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(item.id);
            }}
            className="btn btn-ghost btn-icon"
            style={{
              padding: "0.25rem",
              minHeight: "auto",
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon size={16} />
            ) : (
              <ChevronRightIcon size={16} />
            )}
          </button>
        ) : (
          <span style={{ width: "24px", display: "inline-block" }}></span>
        )}
        <div className="list-item-content">
          <div className="list-item-title">{item.name}</div>
          <div className="list-item-subtitle">
            <span className={`badge ${getColor(item.entity_type)}`}>
              {item.entity_type}
            </span>
            {hasChildren && (
              <span
                style={{
                  marginLeft: "var(--spacing-sm)",
                  color: "var(--text-muted)",
                }}
              >
                {children.length} items
              </span>
            )}
          </div>
        </div>
        {isSelected && selectedItems.size > 1 && (
          <div
            style={{
              position: "absolute",
              top: "-6px",
              right: "-6px",
              background: "var(--accent-primary)",
              color: "white",
              borderRadius: "50%",
              width: "20px",
              height: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          >
            {selectedItems.size}
          </div>
        )}
      </div>
    );

    return (
      <div key={item.id} style={{ marginBottom: "var(--spacing-xs)" }}>
        <DraggableItem item={item} canDrag={canDragItem}>
          <DroppableItem item={item} onDrop={handleDrop} canDrop={canDrop}>
            {itemContent}
          </DroppableItem>
        </DraggableItem>
        {hasChildren && isExpanded && hasFilteredChildren && (
          <div>
            {filteredChildren.map((child) =>
              renderContainerTreeItem(child, level + 1),
            )}
          </div>
        )}
      </div>
    );
  };

  const renderUnorganizedItem = (item) => {
    const children = getChildren(item.id);
    const isSelected = selectedItems.has(item.id);

    const canDrag =
      item.entity_type === "product" || item.entity_type === "container";

    const handleClick = (e) => {
      if (e.shiftKey || e.ctrlKey || e.metaKey) {
        e.preventDefault();
        toggleSelectItem(item.id);
      } else if (selectedItems.size > 0) {
        // If items are selected and regular click, clear selection
        clearSelection();
      }
    };

    // Can drag if: item is draggable AND (no selection OR this item is selected)
    const canDragItem = canDrag && (selectedItems.size === 0 || isSelected);

    return (
      <DraggableItem key={item.id} item={item} canDrag={canDragItem}>
        <div
          style={{ marginBottom: "var(--spacing-xs)", position: "relative" }}
        >
          <div
            className="list-item card-compact"
            style={{
              userSelect: "none",
              background: isSelected ? "rgba(59, 130, 246, 0.15)" : undefined,
              borderColor: isSelected ? "var(--accent-primary)" : undefined,
            }}
            onClick={handleClick}
          >
            <div className="list-item-content">
              <div className="list-item-title">{item.name}</div>
              <div className="list-item-subtitle">
                <span className={`badge ${getColor(item.entity_type)}`}>
                  {item.entity_type}
                </span>
                {children.length > 0 && (
                  <span
                    style={{
                      marginLeft: "var(--spacing-sm)",
                      color: "var(--text-muted)",
                    }}
                  >
                    {children.length} items
                  </span>
                )}
              </div>
            </div>
            {/* Show count badge when this is selected and there are multiple selections */}
            {isSelected && selectedItems.size > 1 && (
              <div
                style={{
                  position: "absolute",
                  top: "-6px",
                  right: "-6px",
                  background: "var(--accent-primary)",
                  color: "white",
                  borderRadius: "50%",
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              >
                {selectedItems.size}
              </div>
            )}
          </div>
        </div>
      </DraggableItem>
    );
  };

  const UnorganizedDropZone = ({ children }) => {
    const [{ isOver }, drop] = useDrop(
      () => ({
        accept: ItemTypes.ENTITY,
        canDrop: (item) => item.parent_id != null,
        drop: (item) => {
          handleDropUnorganized(item);
        },
        collect: (monitor) => ({
          isOver: monitor.isOver() && monitor.canDrop(),
        }),
      }),
      [],
    );

    return (
      <div
        ref={drop}
        style={{
          minHeight: "300px",
          padding: "var(--spacing-md)",
          border: `2px dashed ${isOver ? "var(--accent-primary)" : "var(--border-color)"}`,
          borderRadius: "var(--radius-lg)",
          background: isOver
            ? "rgba(59, 130, 246, 0.05)"
            : "var(--bg-secondary)",
          transition: "all 0.2s ease",
        }}
      >
        {children}
      </div>
    );
  };

  const unorganizedProducts = products.filter((p) => !p.parent_id);
  const unorganizedContainers = containers.filter((c) => !c.parent_id);
  const topLevelLocations = locations.filter((l) => !l.parent_id);

  const allUnorganized = [...unorganizedProducts, ...unorganizedContainers];

  // When searching, show all matching locations; otherwise show only top-level
  const filteredLocations = searchLocations ? locations : topLevelLocations;
  const filteredContainersList = containers;
  const filteredUnorganized = allUnorganized;

  if (loading) {
    return (
      <div className="container text-center" style={{ paddingTop: "3rem" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">Organize</h1>
            <p className="header-subtitle">
              Drag and drop items to organize your inventory
            </p>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Undo Bar */}
        {undoStack.length > 0 && (
          <div
            className="card"
            style={{
              marginBottom: "var(--spacing-lg)",
              padding: "var(--spacing-sm) var(--spacing-md)",
              background: "var(--bg-tertiary)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: undoStack.length > 1 ? "var(--spacing-xs)" : 0,
              }}
            >
              <span style={{ fontSize: "0.875rem" }}>
                {undoStack.length === 1
                  ? `Moved "${undoStack[0].itemName}"`
                  : `${undoStack.length} items moved`}
              </span>
              {undoStack.length === 1 ? (
                <button
                  onClick={() => handleUndo(0)}
                  className="btn btn-ghost"
                  style={{
                    padding: "4px 8px",
                    fontSize: "0.875rem",
                    color: "var(--accent-primary)",
                  }}
                >
                  Undo
                </button>
              ) : (
                <button
                  onClick={handleUndoAll}
                  className="btn btn-ghost"
                  style={{
                    padding: "4px 8px",
                    fontSize: "0.875rem",
                    color: "var(--accent-primary)",
                  }}
                >
                  Undo All
                </button>
              )}
            </div>
            {undoStack.length > 1 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                {undoStack.map((action, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: "0.8rem",
                      color: "var(--text-muted)",
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      {action.itemName}
                    </span>
                    <button
                      onClick={() => handleUndo(index)}
                      style={{
                        background: "none",
                        border: "none",
                        padding: "2px 6px",
                        fontSize: "0.8rem",
                        color: "var(--accent-primary)",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      Undo
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div
          className="grid organize-grid"
          style={{
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "var(--spacing-xl)",
          }}
        >
          <div style={{ minWidth: 0, overflow: "hidden" }}>
            <h2
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                marginBottom: "var(--spacing-md)",
                display: "flex",
                alignItems: "center",
                gap: "var(--spacing-sm)",
              }}
            >
              <LocationIcon size={20} /> Locations ({topLevelLocations.length})
            </h2>

            <div
              style={{
                marginBottom: "var(--spacing-md)",
                position: "relative",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-muted)",
                }}
              >
                <SearchIcon size={16} />
              </span>
              <input
                type="text"
                className="input"
                placeholder="Search locations..."
                value={searchLocations}
                onChange={(e) => handleSearchLocations(e.target.value)}
                style={{ width: "100%", paddingLeft: "36px" }}
              />
            </div>

            {filteredLocations.length === 0 ? (
              <div
                className="card text-center"
                style={{ padding: "var(--spacing-xl)" }}
              >
                <div
                  style={{
                    marginBottom: "var(--spacing-sm)",
                    color: "var(--text-secondary)",
                  }}
                >
                  {searchLocations ? (
                    <SearchIcon size={48} />
                  ) : (
                    <LocationIcon size={48} />
                  )}
                </div>
                <p className="text-muted">
                  {searchLocations
                    ? "No matching locations"
                    : "No locations yet"}
                </p>
                {!searchLocations && (
                  <button
                    onClick={() => navigate("/create")}
                    className="btn btn-primary btn-sm mt-2"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <PlusIcon size={14} /> Add Location
                  </button>
                )}
              </div>
            ) : (
              <>
                {filteredLocations.map((loc) => renderLocationTreeItem(loc, 0))}
                {hasMore.locations && !searchLocations && (
                  <button
                    onClick={() => loadMore("location")}
                    disabled={loadingMore}
                    className="btn btn-secondary"
                    style={{ width: "100%", marginTop: "var(--spacing-md)" }}
                  >
                    {loadingMore ? "Loading..." : "Load More Locations"}
                  </button>
                )}
              </>
            )}
          </div>

          <div style={{ minWidth: 0, overflow: "hidden" }}>
            <h2
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                marginBottom: "var(--spacing-md)",
                display: "flex",
                alignItems: "center",
                gap: "var(--spacing-sm)",
              }}
            >
              <ContainerIcon size={20} /> Containers ({containers.length})
            </h2>

            <div
              style={{
                marginBottom: "var(--spacing-md)",
                position: "relative",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-muted)",
                }}
              >
                <SearchIcon size={16} />
              </span>
              <input
                type="text"
                className="input"
                placeholder="Search containers..."
                value={searchContainers}
                onChange={(e) => handleSearchContainers(e.target.value)}
                style={{ width: "100%", paddingLeft: "36px" }}
              />
            </div>

            {filteredContainersList.length === 0 ? (
              <div
                className="card text-center"
                style={{ padding: "var(--spacing-xl)" }}
              >
                <div
                  style={{
                    marginBottom: "var(--spacing-sm)",
                    color: "var(--text-secondary)",
                  }}
                >
                  {searchContainers ? (
                    <SearchIcon size={48} />
                  ) : (
                    <ContainerIcon size={48} />
                  )}
                </div>
                <p className="text-muted">
                  {searchContainers
                    ? "No matching containers"
                    : "No containers yet"}
                </p>
                {!searchContainers && (
                  <button
                    onClick={() => navigate("/create")}
                    className="btn btn-primary btn-sm mt-2"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <PlusIcon size={14} /> Add Container
                  </button>
                )}
              </div>
            ) : (
              <>
                {filteredContainersList.map((cont) =>
                  renderContainerTreeItem(cont, 0),
                )}
                {hasMore.containers && !searchContainers && (
                  <button
                    onClick={() => loadMore("container")}
                    disabled={loadingMore}
                    className="btn btn-secondary"
                    style={{ width: "100%", marginTop: "var(--spacing-md)" }}
                  >
                    {loadingMore ? "Loading..." : "Load More Containers"}
                  </button>
                )}
              </>
            )}
          </div>

          <div style={{ minWidth: 0, overflow: "hidden" }}>
            <h2
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                marginBottom: "var(--spacing-md)",
                display: "flex",
                alignItems: "center",
                gap: "var(--spacing-sm)",
              }}
            >
              <ProductIcon size={20} /> Unorganized ({allUnorganized.length})
            </h2>

            <div
              style={{
                marginBottom: "var(--spacing-md)",
                position: "relative",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-muted)",
                }}
              >
                <SearchIcon size={16} />
              </span>
              <input
                type="text"
                className="input"
                placeholder="Search unorganized..."
                value={searchUnorganized}
                onChange={(e) => handleSearchUnorganized(e.target.value)}
                style={{ width: "100%", paddingLeft: "36px" }}
              />
            </div>

            <UnorganizedDropZone>
              {filteredUnorganized.length === 0 ? (
                <div
                  className="card text-center"
                  style={{ padding: "var(--spacing-xl)" }}
                >
                  <div
                    style={{
                      marginBottom: "var(--spacing-sm)",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {searchUnorganized ? (
                      <SearchIcon size={48} />
                    ) : (
                      <CheckIcon size={48} />
                    )}
                  </div>
                  <p className="text-muted">
                    {searchUnorganized
                      ? "No matching items"
                      : hasMoreUnorganized.products ||
                          hasMoreUnorganized.containers
                        ? "More unorganized items to load"
                        : "All items organized!"}
                  </p>
                  {(hasMoreUnorganized.products ||
                    hasMoreUnorganized.containers) &&
                    !searchUnorganized && (
                      <button
                        onClick={() => {
                          if (hasMoreUnorganized.products)
                            loadMore("product", { unorganizedOnly: true });
                          if (hasMoreUnorganized.containers)
                            loadMore("container", { unorganizedOnly: true });
                        }}
                        disabled={loadingMore}
                        className="btn btn-secondary"
                        style={{
                          width: "100%",
                          marginTop: "var(--spacing-md)",
                        }}
                      >
                        {loadingMore ? "Loading..." : "Load More"}
                      </button>
                    )}
                </div>
              ) : (
                <>
                  {filteredUnorganized.map((item) =>
                    renderUnorganizedItem(item),
                  )}
                  {(hasMoreUnorganized.products ||
                    hasMoreUnorganized.containers) &&
                    !searchUnorganized && (
                      <button
                        onClick={() => {
                          if (hasMoreUnorganized.products)
                            loadMore("product", { unorganizedOnly: true });
                          if (hasMoreUnorganized.containers)
                            loadMore("container", { unorganizedOnly: true });
                        }}
                        disabled={loadingMore}
                        className="btn btn-secondary"
                        style={{
                          width: "100%",
                          marginTop: "var(--spacing-md)",
                        }}
                      >
                        {loadingMore ? "Loading..." : "Load More"}
                      </button>
                    )}
                </>
              )}
            </UnorganizedDropZone>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrganizeWithDnd() {
  // Detect if device supports touch
  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  const backend = isTouchDevice ? TouchBackend : HTML5Backend;
  const backendOptions = isTouchDevice
    ? { enableMouseEvents: true }
    : undefined;

  return (
    <DndProvider backend={backend} options={backendOptions}>
      <Organize />
    </DndProvider>
  );
}

export default OrganizeWithDnd;
